import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Layers, Globe, User, Info, AlertTriangle, AlertCircle, CheckCircle2,
  Search, Filter, Database, Power, Zap, ChevronDown, Mail, X, Code2
} from 'lucide-react';
import { toast } from 'sonner';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { http } from '@/libs/http';
import { PageLayout } from '@/components/page-layout';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SubscriberInfo {
  email: string;
  name?: string;
  identity_id: string;
  subscribed_at?: string;
}

interface EventConfig {
  event_id: string;
  level: string;
  type: string;
  log_activity: boolean;
  notify?: boolean;
  is_enabled: boolean;
  description: string;
  sample_payload?: Record<string, any>;
  email_templates?: Array<any>;
  subscribers_count?: number;
  subscribers?: SubscriberInfo[];
}

// ─── Config ───────────────────────────────────────────────────────────────────

const levelConfig = {
  platform: {
    label: 'Platform',
    icon: Layers,
    color: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-50 dark:bg-violet-950/30',
    badge: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
    border: 'border-violet-200 dark:border-violet-900/50',
  },
  tenant: {
    label: 'Tenant',
    icon: Globe,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-900/50',
  },
  user: {
    label: 'User',
    icon: User,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    border: 'border-emerald-200 dark:border-emerald-900/50',
  },
};

const typeConfig: Record<string, { label: string; color: string; bg: string; icon: React.FC<any> }> = {
  success: { label: 'Success', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/30', icon: CheckCircle2 },
  error: { label: 'Error', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/30', icon: AlertCircle },
  warning: { label: 'Warning', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30', icon: AlertTriangle },
  info: { label: 'Info', color: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-950/30', icon: Info },
};

// ─── Mini Toggle ─────────────────────────────────────────────────────────────

const MiniToggle = ({ checked, onChange, activeColor = 'bg-(--primary)' }: {
  checked: boolean;
  onChange: () => void;
  activeColor?: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={e => { e.stopPropagation(); onChange(); }}
    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${checked ? activeColor : 'bg-slate-200 dark:bg-slate-800'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
  </button>
);

// ─── Severity Selector ────────────────────────────────────────────────────────

const SeveritySelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const cfg = typeConfig[value];
  const TypeIcon = cfg?.icon ?? Info;
  return (
    <div className="relative inline-flex items-center">
      <span className={`absolute left-2 pointer-events-none ${cfg?.color}`}>
        <TypeIcon className="w-3.5 h-3.5" />
      </span>
      <select
        value={value}
        onChange={e => { e.stopPropagation(); onChange(e.target.value); }}
        onClick={e => e.stopPropagation()}
        className={`pl-7 pr-6 py-1 text-xs font-semibold rounded-lg border appearance-none focus:outline-none cursor-pointer ${cfg?.bg} ${cfg?.color} border-current/20`}
      >
        <option value="success">Success</option>
        <option value="warning">Warning</option>
        <option value="error">Error</option>
        <option value="info">Info</option>
      </select>
    </div>
  );
};

// ─── Subscribers Modal ───────────────────────────────────────────────────────

const SubscribersModal = ({
  event,
  onClose,
}: {
  event: EventConfig;
  onClose: () => void;
}) => {
  const subscribers = event.subscribers || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          type="button"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-sky-50 dark:bg-sky-950/50 rounded-xl text-(--primary) border border-sky-200 dark:border-sky-800">
            <User className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold dark:text-white">Email Notification Subscribers</h3>
            <p className="text-xs text-slate-500 font-mono">{event.event_id}</p>
          </div>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400">
          The following users have subscribed to email notifications for this event in their notification preferences and will be blind-copied (BCC) on dispatches:
        </p>

        <div className="space-y-2 max-h-80 overflow-y-auto pt-1">
          {subscribers.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
              <User className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-700 mb-2" />
              <p className="text-xs text-slate-400">No subscribed users for this event yet.</p>
            </div>
          ) : (
            subscribers.map((sub, idx) => (
              <div
                key={sub.identity_id || idx}
                className="p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{sub.name || sub.email}</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 font-mono truncate">{sub.email}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-[10px] bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 font-bold px-2 py-0.5 rounded-full">
                    BCC Subscribed
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Event Row Card ───────────────────────────────────────────────────────────

const EventRow = ({
  event,
  onUpdate,
  onShowSubscribers,
}: {
  event: EventConfig;
  onUpdate: (eventId: string, updates: Partial<EventConfig>) => void;
  onShowSubscribers: (event: EventConfig) => void;
}) => {
  const [showSchema, setShowSchema] = useState(false);
  const TypeIcon = typeConfig[event.type]?.icon ?? Info;
  const templates = event.email_templates || [];
  const payloadKeys = Object.keys(event.sample_payload || {});

  return (
    <div className="border-b border-slate-100 dark:border-slate-900/80 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-900/20 transition-colors group">
      <div className="flex items-center gap-4 px-5 py-3.5">
        {/* Severity icon */}
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${typeConfig[event.type]?.bg ?? 'bg-slate-100 dark:bg-slate-900'}`}>
          <TypeIcon className={`w-4 h-4 ${typeConfig[event.type]?.color ?? 'text-slate-400'}`} />
        </div>

        {/* Event ID + description + Connected Email Templates + Subscribers */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-bold text-slate-900 dark:text-white">{event.event_id}</span>
            {!event.is_enabled && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 uppercase tracking-wide">Disabled</span>
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5 max-w-md">{event.description}</p>

          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {/* Subscribers Count Pill */}
            <button
              type="button"
              onClick={() => onShowSubscribers(event)}
              className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer"
              title="Click to view subscribed users"
            >
              <User className="w-3 h-3 text-(--primary)" />
              {event.subscribers_count || 0}
            </button>

            {/* Payload Schema Expander Button */}
            <button
              type="button"
              onClick={() => setShowSchema(!showSchema)}
              className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <Code2 className="w-3 h-3 text-(--primary)" />
              {`(${payloadKeys.length} fields)`}
            </button>

            {/* Connected Email Templates Badges */}
            {templates.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1">
                  <Mail className="w-3 h-3 text-(--primary)" />
                  {/* Triggered Template{templates.length > 1 ? 's' : ''} */}
                </span>
                {templates.map(tmpl => (
                  <Link
                    key={tmpl.id}
                    to={`/m/mail/${tmpl.id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-sky-50 dark:bg-sky-950 text-(--primary) border border-sky-200 dark:border-sky-800 hover:bg-sky-100 dark:hover:bg-sky-900 transition-colors"
                    title={`Open and manage email template ${tmpl.name}`}
                  >
                    {tmpl.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Severity selector */}
        <div className="shrink-0">
          <SeveritySelect
            value={event.type}
            onChange={v => onUpdate(event.event_id, { type: v })}
          />
        </div>

        {/* Log Activity toggle */}
        <div className="shrink-0 flex flex-col items-center gap-1 min-w-[70px]">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Log</span>
          <MiniToggle
            checked={event.log_activity}
            onChange={() => onUpdate(event.event_id, { log_activity: !event.log_activity })}
            activeColor="bg-amber-500"
          />
        </div>

        {/* Notify toggle */}
        <div className="shrink-0 flex flex-col items-center gap-1 min-w-[70px]">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Notify</span>
          <MiniToggle
            checked={event.notify ?? true}
            onChange={() => onUpdate(event.event_id, { notify: !(event.notify ?? true) })}
            activeColor="bg-sky-500"
          />
        </div>

        {/* Enabled toggle */}
        <div className="shrink-0 flex flex-col items-center gap-1 min-w-[70px]">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Enabled</span>
          <MiniToggle
            checked={event.is_enabled}
            onChange={() => onUpdate(event.event_id, { is_enabled: !event.is_enabled })}
            activeColor="bg-emerald-500"
          />
        </div>
      </div>

      {/* Expanded Payload Schema Viewer */}
      {showSchema && (
        <div className="mx-5 mb-3.5 p-3.5 bg-slate-900 text-slate-200 rounded-xl font-mono text-xs space-y-2 border border-slate-800 animate-in fade-in duration-150">
          <div className="flex items-center justify-between border-b border-slate-800 pb-2">
            <span className="text-[11px] text-slate-400 font-sans font-semibold">Emitted Payload JSON Schema for {event.event_id}</span>
            <span className="text-[10px] font-bold text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-800">Available in Email Templates as {'{{variable}}'}</span>
          </div>
          <pre className="text-[11px] leading-relaxed overflow-x-auto text-sky-300">
            {JSON.stringify(event.sample_payload || {}, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

// ─── Level Group ──────────────────────────────────────────────────────────────

const LevelGroup = ({
  level,
  events,
  onUpdate,
  onShowSubscribers,
}: {
  level: string;
  events: EventConfig[];
  onUpdate: (eventId: string, updates: Partial<EventConfig>) => void;
  onShowSubscribers: (event: EventConfig) => void;
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const cfg = levelConfig[level as keyof typeof levelConfig];
  const LevelIcon = cfg?.icon ?? Zap;
  const enabledCount = events.filter(e => e.is_enabled).length;

  return (
    <div className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-900 rounded-2xl overflow-hidden">
      {/* Group header */}
      <button
        type="button"
        onClick={() => setCollapsed(v => !v)}
        className={`w-full flex items-center gap-4 px-5 py-4 transition-colors hover:bg-slate-50 dark:hover:bg-slate-900/40 ${cfg?.bg}`}
      >
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-white/70 dark:bg-slate-950/50 border ${cfg?.border}`}>
          <LevelIcon className={`w-4.5 h-4.5 ${cfg?.color}`} />
        </div>
        <div className="flex-1 text-left">
          <h3 className={`text-sm font-bold ${cfg?.color}`}>{cfg?.label ?? level} Events</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {events.length} events — {enabledCount} enabled
          </p>
        </div>
        {/* Quick stats pills */}
        <div className="flex items-center gap-2 shrink-0">
          {Object.entries(
            events.reduce((acc, e) => { acc[e.type] = (acc[e.type] || 0) + 1; return acc; }, {} as Record<string, number>)
          ).map(([type, count]) => {
            const tCfg = typeConfig[type];
            const TIcon = tCfg?.icon ?? Info;
            return (
              <span key={type} className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${tCfg?.bg} ${tCfg?.color}`}>
                <TIcon className="w-3 h-3" />
                {count}
              </span>
            );
          })}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${collapsed ? '-rotate-90' : ''}`} />
      </button>

      {/* Column headers */}
      {!collapsed && (
        <>
          <div className="flex items-center gap-4 px-5 py-2 bg-slate-50/60 dark:bg-slate-900/30 border-t border-slate-100 dark:border-slate-900">
            <div className="w-8 shrink-0" />
            <div className="flex-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Event / Description</div>
            <div className="shrink-0 w-28 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center">Severity</div>
            <div className="shrink-0 min-w-[70px] text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center">Log</div>
            <div className="shrink-0 min-w-[70px] text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center">Notify</div>
            <div className="shrink-0 min-w-[70px] text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center">Enabled</div>
          </div>
          <div>
            {events.map(event => (
              <EventRow key={event.event_id} event={event} onUpdate={onUpdate} onShowSubscribers={onShowSubscribers} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const EventsPage = () => {
  const { setBreadcrumbs } = useLayout();
  const [events, setEvents] = useState<EventConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<'all' | 'platform' | 'tenant' | 'user'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [selectedSubscribersEvent, setSelectedSubscribersEvent] = useState<EventConfig | null>(null);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const data = await http.get<EventConfig[]>('/mgmt/v1/events');
      setEvents(data || []);
    } catch {
      toast.error('Failed to load event configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setBreadcrumbs([{ name: 'System Settings', to: '/m/events' }, { name: 'Events' }]);
    fetchEvents();
  }, [setBreadcrumbs]);

  const handleUpdateField = async (eventId: string, updates: Partial<EventConfig>) => {
    setEvents(prev => prev.map(e => (e.event_id === eventId ? { ...e, ...updates } : e)));
    try {
      await http.put(`/mgmt/v1/events/${eventId}`, updates);
      toast.success(`Event updated`);
    } catch {
      toast.error(`Failed to update event`);
      fetchEvents();
    }
  };

  // Stats
  const stats = useMemo(() => ({
    total: events.length,
    enabled: events.filter(e => e.is_enabled).length,
    logged: events.filter(e => e.log_activity).length,
    platform: events.filter(e => e.level === 'platform').length,
    tenant: events.filter(e => e.level === 'tenant').length,
    user: events.filter(e => e.level === 'user').length,
  }), [events]);

  // Filtered events
  const filteredEvents = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return events.filter(e => {
      const matchSearch = !q || e.event_id.toLowerCase().includes(q) || e.description.toLowerCase().includes(q);
      const matchLevel = levelFilter === 'all' || e.level === levelFilter;
      const matchStatus = statusFilter === 'all' || (statusFilter === 'enabled' ? e.is_enabled : !e.is_enabled);
      return matchSearch && matchLevel && matchStatus;
    });
  }, [events, searchQuery, levelFilter, statusFilter]);

  // Grouped by level
  const grouped = useMemo(() => {
    const order = ['platform', 'tenant', 'user'];
    return order
      .map(level => ({ level, events: filteredEvents.filter(e => e.level === level) }))
      .filter(g => g.events.length > 0);
  }, [filteredEvents]);

  if (loading && events.length === 0) {
    return (
      <PageLayout title="Events" description="Configure event types, log activity switches and enabling/disabling status.">
        <div className="flex justify-center items-center h-64"><Loader /></div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Events"
      description="Manage and configure system-wide security, tenant, and user events for audit logs and alerts."
    >
      <div className="space-y-6 animate-in fade-in duration-300">

        {/* ── Stats strip ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: stats.total, Icon: Zap, iconColor: 'bg-slate-100 dark:bg-slate-900 text-slate-500', color: 'text-slate-700 dark:text-slate-200' },
            { label: 'Enabled', value: stats.enabled, Icon: Power, iconColor: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-500', color: 'text-emerald-600 dark:text-emerald-400' },
            { label: 'Logged', value: stats.logged, Icon: Database, iconColor: 'bg-amber-50 dark:bg-amber-950/30 text-amber-500', color: 'text-amber-600 dark:text-amber-400' },
            { label: 'Platform', value: stats.platform, Icon: Layers, iconColor: 'bg-violet-50 dark:bg-violet-950/30 text-violet-500', color: 'text-violet-600 dark:text-violet-400' },
            { label: 'Tenant', value: stats.tenant, Icon: Globe, iconColor: 'bg-blue-50 dark:bg-blue-950/30 text-blue-500', color: 'text-blue-600 dark:text-blue-400' },
            { label: 'User', value: stats.user, Icon: User, iconColor: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-500', color: 'text-emerald-600 dark:text-emerald-400' },
          ].map(({ label, value, Icon, iconColor, color }) => (
            <div key={label} className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-900 rounded-2xl px-4 py-3.5 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${iconColor}`}>
                <Icon className="w-4.5 h-4.5" />
              </div>
              <div>
                <div className={`text-xl font-black leading-none ${color}`}>{value}</div>
                <div className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wide mt-1">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Toolbar ── */}
        <div className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-900 rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 rounded-xl text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-(--primary)/20 focus:border-(--primary) transition-all"
            />
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Filter className="w-4 h-4 text-slate-400 shrink-0" />

            {/* Level filter */}
            <div className="flex bg-slate-100 dark:bg-slate-900 p-0.5 rounded-xl gap-0.5">
              {(['all', 'platform', 'tenant', 'user'] as const).map(level => (
                <button
                  key={level}
                  onClick={() => setLevelFilter(level)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${levelFilter === level
                    ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
                    }`}
                >
                  {level}
                </button>
              ))}
            </div>

            {/* Status filter */}
            <div className="flex bg-slate-100 dark:bg-slate-900 p-0.5 rounded-xl gap-0.5">
              {(['all', 'enabled', 'disabled'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${statusFilter === s
                    ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
                    }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Results count */}
          <div className="text-xs text-slate-400 dark:text-slate-500 shrink-0 ml-auto">
            {filteredEvents.length} of {events.length} events
          </div>
        </div>

        {/* ── Event groups ── */}
        {grouped.length === 0 ? (
          <div className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-900 rounded-2xl py-16 text-center text-slate-400 dark:text-slate-500 text-sm italic">
            No events match your current filters.
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map(({ level, events: groupEvents }) => (
              <LevelGroup
                key={level}
                level={level}
                events={groupEvents}
                onUpdate={handleUpdateField}
                onShowSubscribers={setSelectedSubscribersEvent}
              />
            ))}
          </div>
        )}

        {/* ── Subscribers Modal ── */}
        {selectedSubscribersEvent && (
          <SubscribersModal
            event={selectedSubscribersEvent}
            onClose={() => setSelectedSubscribersEvent(null)}
          />
        )}
      </div>
    </PageLayout>
  );
};
